'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { usePurchases } from '@/stores/support-page/purchase';

export const AutoSroll = () => {
  const { purchaseComponent } = usePurchases();
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleItems, setVisibleItems] = useState(5);
  const [sliderReady, setSliderReady] = useState(false);

  const getDuplicatedItems = useMemo(() => {
    if (!purchaseComponent) return [];

    const items = purchaseComponent;
    const minItemsForLoop = Math.max(visibleItems * 2, 10);

    if (items.length >= minItemsForLoop) {
      return [...items, ...items];
    }

    const neededDuplicates = Math.ceil(minItemsForLoop / items.length);
    return Array(neededDuplicates).fill(items).flat();
  }, [purchaseComponent, visibleItems]);

  useEffect(() => {
    if (!containerRef.current || !sliderRef.current) return;

    const calculateVisibleItems = () => {
      const containerWidth = containerRef.current?.offsetWidth || 1200;
      const itemWidth = 150;
      const calculatedVisible = Math.max(
        1,
        Math.floor(containerWidth / itemWidth)
      );
      setVisibleItems(calculatedVisible);
    };

    calculateVisibleItems();
    window.addEventListener('resize', calculateVisibleItems);

    return () => window.removeEventListener('resize', calculateVisibleItems);
  }, []);

  useEffect(() => {
    if (purchaseComponent && sliderRef.current) {
      setSliderReady(true);
    }
  }, [purchaseComponent]);

  if (!purchaseComponent) {
    return <div className='h-[200px] w-full bg-gray-100' />;
  }

  return (
    <div
      ref={containerRef}
      className='relative w-full overflow-hidden py-4'
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        ref={sliderRef}
        className={`flex w-max items-center gap-4 ${
          sliderReady ? 'animate-scroll' : ''
        } ${isPaused ? 'animation-paused' : ''}`}
        style={
          {
            '--animation-duration': `${75}s`,
            '--item-count': getDuplicatedItems.length,
          } as React.CSSProperties
        }
      >
        {getDuplicatedItems.map((data, idx) => {
          const thumbnail = data.attributes?.thumbnail;
          const retailerName = data.attributes?.name || 'Retailer';

          return (
            <div
              key={`${data.RetailerId}-${idx}`}
              className='min-w-24 flex-shrink-0'
            >
              <a
                href={data.ClickThruUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='retailer-card group flex h-full w-full rounded-xl hover:cursor-pointer'
              >
                <div className='mx-auto flex flex-col'>
                  <div className='retailer-card-body relative my-auto flex h-full w-full flex-col items-center justify-center overflow-hidden'>
                    <img
                      className='retailer-logo h-24 rounded-xl object-cover object-center duration-300 group-hover:scale-110'
                      src={thumbnail?.data?.attributes.url}
                      alt={`${retailerName} logo`}
                      loading='eager'
                    />
                  </div>
                </div>
              </a>
            </div>
          );
        })}
      </div>

      <div className='pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent' />
      <div className='pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent' />
    </div>
  );
};
